// ScrollyViewer — extrahiert aus app/src/components/editor/Viewer/ScrollyViewer.tsx
// (Plan A Mapping-Audit 2026-05-22).
//
// Abstrahiert app/-Coupling:
//   - `useAuth().isAuthenticated` → optional `isAuthenticated?: boolean` Prop
//   - `EditorEventBus` → optional `editorBridge?: render-prop` Slot
//   - `renderFooter?: ReactNode` Slot (Colophon from @scrolly/viewer)
//   - `SelectedPanel`-Typ inline-kopiert (ScrollyViewerCurrentSlide)
//   - `DEFAULT_MAPPINGS` weggelassen (waren leere Defaults, fallen direkt
//     auf "SofiaSansSemiCondensed" durch)
//
// Trust-Hierarchie: Funktion = app/. Hier 1:1-Logik aus app/, nur
// Coupling raus.

import type { CtaAbgabe, CustomFonts, FontMapping, PostJson } from '@scrolly/post-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef as useReactRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { flushSync } from 'react-dom';
import {
  VisualStoryPlayer,
  useBlockVertScroll,
  type Renderer,
  type VisualStoryEventHandler,
} from '@scrolly/renderer';
import useInitializeViewerState from './hooks/useInitializeViewerState';
import useCustomFontLoader, {
  type FontInfoDto,
} from './hooks/useCustomFontLoader';
import { CompanyLogo, type BrandingLogo } from './CompanyLogo';
import { StartScreen } from './StartScreen';
import { clampSceneIdxToZeroIndexed } from './clampSceneIdx';
import { schleifenUeberblendung } from './schleifenUeberblendung';
import { t, type Lang } from './i18n';

/**
 * Layout-Settle-Delay vor initialScrollPercent/-SceneIdx-Restore.
 * VisualStoryStore.start() triggert regenerateSlidePositionsCache via
 * Doppel-setTimeout (~10ms inner + ~10ms outer). 100ms ist konservativer
 * Sicherheitspuffer.
 */
const LAYOUT_SETTLE_DELAY_MS = 100;

/** Throttle-Fenster für onScrollProgress: 1s (leading + trailing). */
const SCROLL_PROGRESS_THROTTLE_MS = 1000;

export const VIEWER_ROOT_CONTAINER = 'viewer-root-container';

const VIEWER_FONT_FALLBACK = 'SofiaSansSemiCondensed';

function getWindowScrollMetrics() {
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const viewportHeight = window.innerHeight || doc.clientHeight || 0;
  const scrollHeight = Math.max(doc.scrollHeight, body.scrollHeight);
  return {
    max: scrollHeight - viewportHeight,
    scrollTop,
  };
}

/**
 * Inline-Kopie von app/-SelectedPanel ohne 'TEXT_AND_THUMBNAIL'. Spezielle
 * String-Tokens für Editor-Navigation (Background-Slide → 0, Credits-Slide
 * → scrollToCredits, sonst goToSlide(idx)).
 */
export type ScrollyViewerCurrentSlide =
  | number
  | 'CREDITS_AND_SOURCES'
  | 'BACKGROUND_AND_TITELS'
  | 'SOCIAL_PREVIEW';

export type { BrandingLogo } from './CompanyLogo';

interface Props {
  id: string | number;
  post: PostJson;
  /**
   * Editor-only: render-prop für EditorEventHandler (Live-Updates aus
   * Editor an Renderer durchreichen). app/ wraps mit
   * `<EditorEventHandler renderer={...} />`. public/ lässt undefined.
   */
  editorBridge?: (rendererRef: RefObject<Renderer | null>) => ReactNode;
  /**
   * PRO-EDITOR-Modus (Jans Leinwand): Scroll dauerhaft gesperrt (Playhead ist
   * die einzige Steuerung), Viewer-Textfenster versteckt (Jans TextRenderer
   * uebernimmt), Overlay klick-durchlaessig (Anfasser), Credits kleben an der
   * Buehne. NUR der SharedRendererAdapter setzt das.
   *
   * ⚠️ Bewusst EXPLIZIT statt aus `editorBridge` abgeleitet: auch Benedikts
   * Template-Editor-Vorschau (EditorPreviewViewer) mountet eine Bridge (fuer
   * Live-Events) — die Ableitung `!!editorBridge` hat ihr seit dem 25.07.
   * still Pro-Editor-Verhalten uebergestuelpt, seit dem Textfenster-Umbau
   * (#205) fehlten dort sichtbar die HTML-Boxen (Jans Fund 01.08.2026).
   */
  proEditor?: boolean;
  /**
   * Editor-only: show full Sentence-Bloom quote in preview (no word-by-word
   * replay while editing). Defaults to true when editorBridge is set.
   */
  editorPreview?: boolean;
  /**
   * Editor-only: while editing Titel & Hintergrund, force-hide the bloom SVG
   * overlay (stale FLENSBURG gallery seed must not bleed onto the cover).
   */
  sentenceBloomCoverPreview?: boolean;
  /** Obergrenze für Bild-Texturbreiten — für Kachel-Vorschauen (Template-
   *  Galerie), die sonst Reader-große Texturen in eine Mini-Kachel laden.
   *  Begründung an RendererProps.maxTextureWidth im Renderer-Paket. */
  maxTextureWidth?: number;
  /** Auto-Play-Gleitfahrt: currentSlide-Schritte fahren LINEAR mit dieser
   *  Dauer (ms) statt mit dem Knopf-Sprung (quadOut + Pause). Auf das
   *  Schrittintervall gesetzt, gleitet die Vorschau pausenlos mit konstanter
   *  Geschwindigkeit — Begründung an VisualStoryStore.goToSlide. */
  autoplayGlideMs?: number;
  /** Mitlaufender CTA in der Bühne statt am Fenster verankern — für Hüllen,
   *  in denen position:fixed bricht (öffentlicher Voll-Reader, Phone-Frame).
   *  Begründung an VisualStoryPlayer.runningCtaImRahmen. */
  runningCtaImRahmen?: boolean;
  /** Maßstab für CTA und Nachweis im Rahmen, wenn die Bühne kleiner ist als
   *  ein Handy (Galerie-Kachel) — s. VisualStoryPlayer.ueberlagerungMassstab. */
  ueberlagerungMassstab?: number;
  /** VOTE-/RATING-Abgabe melden — nur echte Leser (s. VisualStoryPlayer.onCtaAntwort). */
  onCtaAntwort?: (abgabe: CtaAbgabe) => void;
  /** Sprache des Lesers — bisher nur für die Kapitel-Punkte („Kapitel“/„Chapter“). */
  lang?: Lang;
  /**
   * Editor-only: bloom quote panel — scroll to typewriter runway start,
   * not the end of slide-bloom (which shows the full quote instantly).
   */
  sentenceBloomQuoteNavigation?: boolean;
  /**
   * Editor-only: aktive Slide-Auswahl in TemplateEditor (Navigation-Hook).
   * public/-Viewer ignoriert.
   */
  currentSlide?: ScrollyViewerCurrentSlide;
  /**
   * Erneut zu `currentSlide` springen, wenn sich die Zahl aendert — auch wenn
   * `currentSlide` gleich bleibt (Handy-Template-Editor, Jan 28.09.2026: die
   * Vorschau ist beim Bearbeiten ausgeblendet, ein Sprung dort landet falsch).
   */
  sprungZaehler?: number;
  onViewerReady?: () => void;
  onSlideChange?: (slideIndex: number) => void;
  /**
   * Zugriff auf den Scroll-Store (Export-Fenster in „My Scrollys", Jan
   * 16.10.2026): springt Slide für Slide per `goToSlide(index)` direkt auf
   * die Ruhelage — ohne die Panel-Zählung von `currentSlide`, die je nach
   * Scrolly-Art um eins versetzt ist. Wird mit `null` gerufen, wenn der
   * Viewer abbaut.
   */
  onStoryStore?: (store: unknown) => void;
  viewerRootContainerRef: RefObject<HTMLElement | null>;
  heightRef: RefObject<HTMLElement | null>;
  blockScrollElementRef: RefObject<HTMLElement | null>;
  brandingLogo?: BrandingLogo | null;
  /**
   * Optional Renderer-Event-Callback (z.B. storyNavigation für GA).
   * Wird via useInitializeViewerState.options.onEvent durchgereicht.
   */
  onEvent?: VisualStoryEventHandler;
  /**
   * imgproxy base URL für Image-Optimization. Leer-String oder undefined
   * fällt auf raw Asset-URL zurück.
   */
  imgproxyUrl?: string;
  /**
   * Auth-State für VisualStoryPlayer's `showQuality` (Quality-Selector in
   * Editor). Optional — public/ ohne Auth lässt false.
   */
  isAuthenticated?: boolean;
  /**
   * Optional Footer-Slot (z.B. Colophon from @scrolly/viewer).
   * Wenn undefined: kein Footer. Wird unterhalb des VisualStoryPlayer
   * gerendert.
   */
  renderFooter?: ReactNode;
  /**
   * Custom StartScreen / Cover. Render-prop bekommt `onStart` (triggert
   * setStarted+scrollToStartSlide) und `progress` (Renderer-Mount-Progress
   * 0..1). Wenn undefined UND nicht embedded UND nicht isOgMode: Default-
   * StartScreen aus packages/viewer/ rendert. Wenn explicit definiert:
   * Custom-Cover (z.B. public/-CoverSection mit Vorlage-Layout).
   */
  renderCover?: (controls: {
    onStart: () => void;
    progress: number;
    isOgMode: boolean;
    fontFamilies: CustomFonts;
    height: number;
  }) => ReactNode;
  /**
   * Skip Default-CompanyLogo aus packages/. public/-Stack rendert eigenen
   * CompanyLogo via chromeOverlays-Pattern (position:absolute innerhalb
   * Phone-Letterbox). Default false → packages/-CompanyLogo rendert
   * position:fixed wenn brandingLogo gesetzt.
   */
  disableCompanyLogo?: boolean;
  /**
   * Embedded mode — strips all viewer chrome (StartScreen, CompanyLogo,
   * Footer) and scopes the font CSS variables to the viewer's outer
   * `<div>` instead of `document.documentElement`. Used by
   * TemplateHoverPreview.
   */
  embedded?: boolean;
  /**
   * OG-Image render mode. Strips StartScreen-CTA, CompanyLogo, Footer.
   * Auto-triggert onStart(false) damit Renderer für Background-Layers
   * in slide[0] aktiv ist.
   */
  isOgMode?: boolean;
  /**
   * Scroll-Source-Polarität (Plan-2 iOS-Fix). DEFAULT "container". Nur der
   * public/-Immersive-Full-Page-Reader (ViewerChrome) reicht "window" durch.
   * Desktop-Phone, Embedded, app/-Editor bleiben "container".
   */
  scrollSource?: 'container' | 'window';
  /**
   * Plan-1.9 Phase 3.A.CP3 — Resume-Position-Restore (0..1). Bei
   * vorhandenem Wert wird nach Layout-Settle viewerFrame.scrollTop =
   * scrollHeight × p gesetzt. Konflikt-Hierarchie:
   * initialSceneIdxOneIndexed > initialScrollPercent > autoAdvanceOnMount.
   */
  initialScrollPercent?: number;
  /**
   * Plan-1.9 Phase 3.A.CP3 — throttled scroll-progress callback (0..1).
   * Wird ~1× pro Sekunde gefeuert. Consumer (public/-ViewerChrome)
   * persistiert via useResumeState.save.
   */
  onScrollProgress?: (percent: number) => void;
  /**
   * Plan-1.9 Phase 3.B.CP2 — Open-Graph-Anchor 1-indexed Scene-Index aus
   * URL. Wird nach Layout-Settle als store.goToSlide(internalIdx)
   * realisiert. Konflikt: gewinnt über initialScrollPercent +
   * autoAdvanceOnMount.
   */
  initialSceneIdxOneIndexed?: number;
  /**
   * Receives Renderer-Mount-Progress (0..1). Wird sofort gefeuert wenn
   * Renderer hochfährt. Hauptkonsument: page-level ProgressHairline.
   */
  onProgress?: (progress: number) => void;
  /**
   * iOS-Audio-Permission-Anchor (2026-05-28). Wenn true: ScrollyViewer
   * triggert sich selbst onStart(false) sobald Renderer ready, ohne dass
   * ein Cover-Button geklickt werden muss. Use-Case: ViewerChrome
   * (public/-Stack) hat ein eigenes Pre-Mount-Cover ausserhalb von
   * ScrollyViewer und mountet ScrollyViewer erst NACH dem Click. Der
   * ScrollyViewer-Mount läuft also bereits im User-Activation-Context,
   * Video.play() darf laufen — aber das interne ScrollyViewer-Cover
   * würde sonst nochmal angezeigt. autoStart={true} überspringt das.
   *
   * Unterschied zu isOgMode: isOgMode versteckt zusätzlich Cover-CTA +
   * CompanyLogo + Footer; autoStart triggert NUR den onStart-Call.
   */
  autoStart?: boolean;
  /**
   * Live visual viewport height (px) from ViewerChrome. When Safari restores
   * the page from bfcache the shell is re-synced and the renderer remeasures.
   */
  viewportHeight?: number | null;
  /** Bumps on each pageshow so renderer remeasures even when height unchanged. */
  viewportSyncGeneration?: number;
  /**
   * Render-Zeit-Fonts (2026-07-20): Custom-Fonts des Autors, vom Viewer-
   * Endpoint mitgeliefert (`authorFonts`). FALLBACK — greift nur, wenn das
   * eingebackene `slideOptions.fontInfos/.fontMapping` leer ist.
   *
   * Hintergrund: Seit `createFromTemplate` die Fonts nicht mehr ins Scrolly-
   * JSON injiziert, blieb `slideOptions` bei neuen Scrollys leer und der
   * Viewer fiel auf die Default-Schrift zurück (Weser-Kurier: h1 =
   * Instrument Serif, 0 Weser-FontFaces). Eingebackene Werte haben weiterhin
   * Vorrang, damit bewusst pro Scrolly gesetzte Schriften gewinnen.
   */
  authorFonts?: {
    fontInfos?: FontInfoDto[];
    fontMapping?: FontMapping | null;
  } | null;
}

const dummy = () => () => {};
// SSR-safe: gleicher Snapshot auf Server und Client First-Render (Hydration-
// Match). Renderer setzt nach Mount echten Wert via visualStoryStore.
const SSR_FALLBACK_HEIGHT = 800;
const dummySnapShot = () => SSR_FALLBACK_HEIGHT;

function ScrollyViewer({
  id,
  post,
  editorBridge,
  proEditor = false,
  editorPreview,
  sentenceBloomCoverPreview,
  maxTextureWidth,
  autoplayGlideMs,
  runningCtaImRahmen,
  ueberlagerungMassstab,
  onCtaAntwort,
  lang = 'de',
  sentenceBloomQuoteNavigation,
  currentSlide,
  sprungZaehler,
  onViewerReady,
  onSlideChange,
  onStoryStore,
  viewerRootContainerRef,
  heightRef,
  blockScrollElementRef,
  brandingLogo,
  onEvent,
  imgproxyUrl,
  isAuthenticated = false,
  renderFooter,
  renderCover,
  disableCompanyLogo = false,
  embedded = false,
  isOgMode = false,
  scrollSource = 'container',
  initialScrollPercent,
  onScrollProgress,
  initialSceneIdxOneIndexed,
  onProgress,
  autoStart = false,
  viewportHeight = null,
  viewportSyncGeneration = 0,
  authorFonts = null,
}: Props) {
  const [realHeight, setRealHeight] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRealHeight(window.innerHeight);
    }
  }, []);

  const [progress, setProgressInternal] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [started, setStarted] = useState(false);

  // Bridge setProgress: internal state + optional consumer callback.
  // Stable callback via ref pattern um inline-arrow-Renders nicht zur
  // useInitializeViewerState-Re-Init zu triggern.
  const onProgressRef = useReactRef(onProgress);
  useEffect(() => {
    onProgressRef.current = onProgress;
  });
  const setProgress = useCallback((p: number | ((prev: number) => number)) => {
    setProgressInternal((prev) => {
      const next = typeof p === 'function' ? p(prev) : p;
      onProgressRef.current?.(next);
      return next;
    });
  }, []);
  const viewerReadyFiredRef = useReactRef(false);
  // Fonts: eingebackene `slideOptions` haben Vorrang (bewusst pro Scrolly
  // gesetzte Schriften gewinnen). Sind sie leer — der Normalfall, seit die
  // Injektion beim Erstellen entfernt wurde —, greifen die vom Viewer-Endpoint
  // mitgelieferten Autoren-Fonts (`authorFonts`). Beides muss zusammenpassen:
  // ohne fontInfos kennt der Browser die Familie nicht und fällt zurück.
  const bakedFontInfos =
    post.data.data.presentationOptions.slideOptions.fontInfos;
  const bakedFontMapping =
    post.data.data.presentationOptions.slideOptions.fontMapping;
  // MERGE baked + authorFonts fontInfos instead of preferring baked. Reason:
  // applyBrandingToPostJson overwrites fontMapping with the ORG font names
  // (e.g. hyphen-style "WeserSans-Semibold"), but the BAKED fontInfos can carry
  // stale, differently-named entries (space-style "Weser Sans Semibold") with
  // broken /resource URLs. Preferring baked then never matches the mapped family
  // → the font silently fails to load (WESER-KURIER). authorFonts carries the
  // canonical entries (correct family names + working /resource/:id URLs), so we
  // must include them. The loader only loads fontInfos whose familyName matches
  // the mapping, so unmatched (stale) entries are harmless; family de-dup is
  // handled by the loader's document.fonts guard.
  const effectiveFontInfos = [
    ...(bakedFontInfos ?? []),
    ...(authorFonts?.fontInfos ?? []),
  ];
  const effectiveFontMapping =
    bakedFontMapping?.headline || bakedFontMapping?.text
      ? bakedFontMapping
      : (authorFonts?.fontMapping ?? undefined);
  const fontFamilies = useCustomFontLoader(
    effectiveFontInfos,
    effectiveFontMapping,
  );

  const {
    htmlRendererRef,
    mainContainerRef,
    canvasContainerRef,
    visualStoryStore,
    renderer,
    onResize,
  } = useInitializeViewerState(
    id,
    post,
    setProgress,
    setCurrentSlideIndex,
    setStarted,
    viewerRootContainerRef,
    heightRef,
    fontFamilies,
    // proEditor bestimmt das INSTANZ-editorMode des VisualStoryStore — nicht
    // mehr die globale WebGLAssets-Flagge, die im Pro-Editor auch den
    // Vorschau-Dialog lahmlegte (Jans Fund 08.08.2026: START tat nichts).
    { imgproxyUrl, onEvent, scrollSource, editorPreview: editorPreview ?? false, proEditor, sentenceBloomCoverPreview, maxTextureWidth },
  );

  const { blockScroll, unblockScroll } = useBlockVertScroll({
    resetScroll: true,
    htmlElement: blockScrollElementRef.current,
  });

  useEffect(() => {
    renderer.current?.getJson(post);
  }, [post, renderer]);

  // ECHTZEIT-WIEDERGABE (Jans „Sonderfall" 09.08.2026): Pro-Editor-Scrollys
  // mit genau Start- + End-Slide und gesetztem WIEDERGABE-Modus sind
  // Animationen mit fester Dauer, keine Lese-Strecken. Für sie ersetzt
  // startRealtimePlayback den 2-Sekunden-Intro-Tween (der die komplette
  // Timeline durchrauschte, während der Ton in Echtzeit weiterlief).
  // playbackMode schreibt NUR der Pro-Editor (MST-Snapshot, immer camelCase;
  // snake_case zur Sicherheit für den Studio-Adapter-Pfad) — Template-,
  // Studio- und Bloom-Scrollys bleiben unberührt (Bloom hat 3 Slides).
  const realtimePlaybackMode = useMemo<'once' | 'loop' | 'pingpong' | null>(
    () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (post as any)?.data?.data;
      const presOpts = data?.presentationOptions ?? data?.presentation_options;
      const mode = presOpts?.playbackMode ?? presOpts?.playback_mode;
      const slides = data?.slides;
      if (!Array.isArray(slides) || slides.length !== 2) return null;
      // Template+ ist IMMER scroll-getrieben (Jans 2-Slide-Fund 12.10.2026):
      // das frühe Gerüst schrieb 'loop' in die presentationOptions — ein
      // 2-Slide-Template+ fuhr nach START im Dauerloop vor und zurück.
      // Bestandsdocs tragen den Wert noch, darum hier ausnehmen.
      const istTemplatePlus = !!(
        post as unknown as { data?: { metadata?: { templatePlus?: unknown } } }
      )?.data?.metadata?.templatePlus;
      if (istTemplatePlus) return null;
      return mode === 'once' || mode === 'loop' || mode === 'pingpong'
        ? mode
        : null;
    },
    [post],
  );

  const currentStoryStore = visualStoryStore.current;
  useEffect(() => {
    if (!onStoryStore) return undefined;
    onStoryStore(currentStoryStore ?? null);
    return () => onStoryStore(null);
  }, [onStoryStore, currentStoryStore]);
  const onStart = useCallback(
    (scrollToStartSlide = true) => {
      if (currentStoryStore && mainContainerRef.current) {
        // Video-Hörbarkeit freischalten (Jans Regel 24.08.2026): Cover-
        // Videos laufen vor dem Start sichtbar, aber STUMM — erst der
        // START-Klick (echte User-Geste) gibt den Ton frei. Vorher
        // kaschierte das im Reader nur die Browser-Autoplay-Richtlinie;
        // in der Editor-Vorschau lief der Ton vor dem Start (Jans Fund).
        renderer.current?.setPlaybackStarted(true);
        // Gesten-Priming SYNCHRON im Klick: alle Videoelemente einmal stumm
        // anspielen, damit Safari ihnen spaeteres programmatisches play()
        // erlaubt (WK 'Bremerin': nur das erste Video lief an, der Rest
        // blieb schwarz — Begruendung am Store-Feld primeVideos).
        renderer.current?.primeVideos();
        flushSync(() => {
          setStarted(true);
        });
        currentStoryStore.regenerateSlidePositionsCache();
        if (scrollToStartSlide) {
          if (realtimePlaybackMode) {
            // Start-+End-Slide-Scrolly: Timeline in Echtzeit abfahren
            // (once/loop/pingpong), Begründung an startRealtimePlayback.
            // Schleife: weiche Überblendung beim Neustart (Jan 17.10.2026)
            currentStoryStore.startRealtimePlayback(realtimePlaybackMode, () =>
              schleifenUeberblendung(renderer.current),
            );
          } else {
            // Intro-Tween: scrollt programmatisch bis Slide-1.textCenter (2.2s,
            // quadInOut, cancelable bei User-Input). User sieht die erste
            // Animation einmal automatisch und lernt „aha, das ist scroll-
            // getrieben". Bene-Entscheidung 2026-05-28.
            currentStoryStore.scrollToStartSlide({ intro: true });
          }
        }
      }
    },
    [currentStoryStore, mainContainerRef, realtimePlaybackMode],
  );

  // EXPLIZITE Prop statt `!!editorBridge` (s. Prop-Doku): auch der
  // Template-Editor mountet eine Bridge — die Ableitung versteckte dort die
  // Viewer-Textfenster und sperrte das Scrollen (Jans Fund 01.08.2026).
  const isProEditor = proEditor;

  useEffect(() => {
    if (started) {
      // EDITOR (Pro-Editor): das Scrollen bleibt dauerhaft gesperrt. Jans
      // Original kennt im Preview kein Scrollen — der Playhead ist die einzige
      // Steuerung (Jans Entscheidung 31.07.2026). "Start" laesst sich weiterhin
      // druecken, das setzt nur `started`. Warum die Sperre: editor-seitig
      // koppeln Scroll und Playhead in BEIDE Richtungen (VisualStoryStore,
      // "wer hat sich zuerst bewegt") — beim Mausrad kaempfen zwei Steuerungen
      // um dieselbe Position ("laufen nicht synchron, das ruckelt").
      //
      // ⚠️ Hier NICHT `blockScroll()` benutzen: das macht bei `resetScroll:true`
      // zusaetzlich ein `scrollTo(0,0)`. Beim ersten Anlauf haderte genau das —
      // der Effekt lief bei jedem Render, setzte den Scroll auf 0, und der
      // Scroll→Timer-Sync zog den Playhead auf Start zurueck (Jans Fund: die
      // "HTML-Fenster anzeigen"-Checkbox anklicken und der Playhead springt).
      // Nur die Overflow-Sperre setzen, die Scroll-POSITION nicht anfassen.
      // Template-Editor bleibt unberuehrt: der mountet ohne `editorBridge`.
      if (isProEditor) {
        const el = blockScrollElementRef.current;
        if (el) {
          el.style.overflowY = 'hidden';
          el.style.overflowX = 'hidden';
        }
      } else {
        unblockScroll();
      }
      onResize();
    } else if (editorPreview && !isProEditor) {
      // VORSCHAU-BOX (Galerie-Dialog, Buehnen-Vorschau, Kachel-Hover): kein
      // Start-Tor. Bene 21.09.2026: „frueher konnte ich in diesem Status
      // scrollen."
      //
      // Das Tor gehoert dem LESER: dort soll der Start bewusst passieren
      // (Ton, iOS-Autoplay, Stage-Gating). In einer Vorschau schuetzt es
      // nichts — es kann aber einsperren. Genau das ist passiert: Der Vorhang
      // war hoeher als seine Box, der START-Knopf lag darunter, und das
      // Einzige, was ihn hereingeholt haette, war gesperrt. Ob der Knopf
      // erreichbar ist, haengt an Fensterhoehe und Schriftmassen — eine
      // Bedingung, die niemand garantieren kann.
      //
      // Ohne Sperre scrollt der Vorhang einfach weg, wie jeder andere Inhalt.
      // Der START-Knopf bleibt und tut weiterhin, was er tut.
      //
      // `!isProEditor`: Im Pro-Editor koppeln Scroll und Playhead in beide
      // Richtungen — dort bleibt es bei der Sperre (Jans Entscheidung
      // 31.07.2026). Der Template-Editor mountet zwar eine Bridge, setzt
      // `proEditor` aber bewusst NICHT (s. oben), und darf scrollen.
      unblockScroll();
      onResize();
    } else {
      blockScroll();
    }
  }, [
    blockScroll,
    onResize,
    started,
    unblockScroll,
    isProEditor,
    blockScrollElementRef,
    editorPreview,
  ]);

  // After ViewerChrome re-syncs the shell to visualViewport (pageshow), remeasure
  // the sticky canvas so it fills the viewport — fixes the dead strip at the bottom.
  useEffect(() => {
    if (viewportHeight == null) return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        visualStoryStore.current?.syncFrameDimensions();
        onResize();
      });
    });
    return () => {
      cancelled = true;
    };
  }, [viewportHeight, viewportSyncGeneration, onResize, visualStoryStore]);

  // Auto-start renderer in OG-Mode oder wenn autoStart-Prop gesetzt
  // (iOS-Audio-Permission-Anchor; ViewerChrome mountet ScrollyViewer
  // erst nach Pre-Mount-Cover-Click, der Mount selbst ist also schon
  // im User-Activation-Context — autoStart überspringt das interne
  // Cover, ohne sonstige isOgMode-Effekte zu erzwingen).
  //
  // Unterschied OG vs autoStart (2026-05-28 — Cover-Doppelmount-Fix):
  // - OG-Mode: onStart(false) ohne scroll. Headless-Screenshot soll
  //   slide[0] mit Cover-Hintergrund zeigen.
  // - autoStart: onStart(true) MIT scrollToStartSlide. Beim Stage-
  //   Gating hat User bereits pre-Mount geklickt, Cover ist in
  //   ViewerChrome (außerhalb ScrollyViewer). Ohne Auto-Scroll würde
  //   der spacer-Bereich in VisualStoryPlayer als Cover-shaped
  //   leerer Bereich wirken — der User sähe "noch ein Cover".
  //   scrollToStartSlide() springt zu slide[0].textCenter und
  //   überspringt damit den spacer.
  useEffect(() => {
    if (isOgMode && !started && currentStoryStore && mainContainerRef.current) {
      onStart(false);
      return;
    }
    // progress >= 1 (Jans Entscheidung 24.08.2026): Der Intro-Tween wartet,
    // bis alle Assets geladen sind — der Vorhang (renderCover, seit 24.08.
    // OHNE Bild) fährt erst über der FERTIG gebooteten Slide hoch. Vorher
    // startete der Tween sofort beim Mount und fuhr während des Boots los.
    // progress läuft unabhängig von `started` (VisualStoryPlayer mountet
    // ungegated, s. OG-Pfad: onStart feuert dort erst NACH progress=1).
    if (
      autoStart &&
      !started &&
      progress >= 1 &&
      currentStoryStore &&
      mainContainerRef.current
    ) {
      onStart(true);
    }
  }, [
    isOgMode,
    autoStart,
    started,
    progress,
    currentStoryStore,
    onStart,
    mainContainerRef,
  ]);

  // Index-Konvention: hat der Scrolly ein „virtuelles Cover"?
  //   virtuelles Cover → slides[0] ist KEIN eigener Frame (frames = slides−1),
  //                      Frame N liegt auf Slide N+1 (PPI/Template-Familie).
  //   kein Cover       → 1:1 (Studio: slides[0] IST der Titel bei scroll=0).
  //
  // FRÜHER wurde das am Layer-INHALT geraten (svg-Background/object3d ⇒
  // „Studio"). Das sind zwei unabhängige Dinge: ein PPI-Template MIT Chart-
  // oder 3D-Layer wurde als Studio fehlklassifiziert → bekam 1:1, hatte aber
  // ein virtuelles Cover → alles um eins verschoben („Preview zeigt immer eins
  // zurück", Editor springt auf die falsche Folie).
  // Belegt 2026-07-16 aus /public/templates: genau 2 von 64 betroffen —
  // „Your Top 5" + „Template with 3d elements" (beide virtuelles Cover UND
  // svg/3D-Layer). Exakt die zwei, in denen der Kunde es reproduziert hat.
  //
  // Jetzt STRUKTURELL: referenziert irgendein Frame die slides[0]-ID?
  //   ja   → slides[0] ist ein echter Frame → 1:1
  //   nein → virtuelles Cover → +1
  // Fallback (kein templateStructure/slides, z.B. Studio/Montage) = alte
  // Layer-Heuristik, damit dort nichts kippt.
  // Template+-Kennung — fürs direkte Panel↔Slide-Mapping (s. unten).
  const istTemplatePlusScrolly = !!(
    post as unknown as { data?: { metadata?: { templatePlus?: unknown } } }
  )?.data?.metadata?.templatePlus;

  const isStudioScrolly = useMemo(() => {
    const p = post as unknown as {
      data?: {
        templateStructure?: { frames?: Array<{ textSlideIds?: string[] }> };
        data?: { slides?: Array<{ id?: string }>; layers?: unknown[] };
      };
    };
    const frames = p?.data?.templateStructure?.frames;
    const firstSlideId = p?.data?.data?.slides?.[0]?.id;
    if (Array.isArray(frames) && frames.length > 0 && firstSlideId) {
      return frames.some(
        (f) =>
          Array.isArray(f?.textSlideIds) &&
          f.textSlideIds.includes(firstSlideId),
      );
    }
    const layers = p?.data?.data?.layers as
      | Array<Record<string, unknown>>
      | undefined;
    if (!Array.isArray(layers)) return false;
    const isStudioLayer = (
      layer: Record<string, unknown> | undefined,
    ): boolean => {
      if (!layer) return false;
      if (layer.type === 'svg' && layer.isBackground === true) return true;
      if (layer.type === 'object3d') return true;
      const content = layer.content as { layer_ids?: string[] } | undefined;
      if (content && Array.isArray(content.layer_ids)) {
        for (const childId of content.layer_ids) {
          const child = layers.find(
            (l) => (l as { id?: string }).id === childId,
          );
          if (isStudioLayer(child)) return true;
        }
      }
      return false;
    };
    return layers.some(isStudioLayer);
  }, [post]);

  useEffect(() => {
    if (currentStoryStore && progress >= 1 && currentSlide !== undefined) {
      if (currentSlide === 'BACKGROUND_AND_TITELS') {
        currentStoryStore.viewerFrame.scrollTop = 0;
        currentStoryStore.scrollTop = 0;
        currentStoryStore.post?.slidePlayer.setTimer(0);
      } else if (currentSlide === 'CREDITS_AND_SOURCES') {
        currentStoryStore.scrollToCredits();
      } else if (typeof currentSlide === 'number') {
        // Template+ (Jans Fund 12.10.2026, „Preview scrollt nicht exakt zum
        // gewählten Slide"): wie Studio-Scrollys DIREKT abbilden — Panel k
        // ruht bei timer = from[k] (Scene angekommen). Der klassische
        // +1-Versatz landete am ENDE des Fensters, wo die NÄCHSTE Scene
        // schon einfährt.
        const direkt = isStudioScrolly || istTemplatePlusScrolly;
        const targetSlide = direkt ? currentSlide : currentSlide + 1;
        if (sentenceBloomQuoteNavigation) {
          // Bloom presentation slides: [slide-1, slide-bloom (empty spacer), slide-2].
          // The quote text lives on the LAST slide (slide-2). Scrolling to the
          // spacer (index 1) lands in dead space ("scrolls too far / no text").
          currentStoryStore.goToSlide(currentStoryStore.slideCount - 1);
        } else {
          currentStoryStore.goToSlide(targetSlide, autoplayGlideMs);
        }
      }
    }
  }, [
    currentSlide,
    sprungZaehler,
    currentStoryStore,
    progress,
    isStudioScrolly,
    istTemplatePlusScrolly,
    sentenceBloomQuoteNavigation,
    autoplayGlideMs,
  ]);

  // Sync slide index back to parent for visual highlight.
  useEffect(() => {
    if (onSlideChange && progress >= 1) {
      const reported =
        isStudioScrolly || istTemplatePlusScrolly
          ? currentSlideIndex + 1
          : currentSlideIndex;
      onSlideChange(reported);
    }
  }, [
    currentSlideIndex,
    onSlideChange,
    progress,
    isStudioScrolly,
    istTemplatePlusScrolly,
  ]);

  // Fire once: when progress first reaches 1, call onStart + onViewerReady.
  useEffect(() => {
    if (progress < 1) {
      viewerReadyFiredRef.current = false;
      return;
    }
    if (onViewerReady && !viewerReadyFiredRef.current) {
      viewerReadyFiredRef.current = true;
      queueMicrotask(() => {
        onStart(false);
        onViewerReady();
      });
    }
  }, [onStart, onViewerReady, progress]);

  // Plan-1.9 Phase 3.A.CP3 — initialScrollPercent Apply (Resume-Restore).
  useEffect(() => {
    if (
      typeof initialScrollPercent !== 'number' ||
      initialScrollPercent <= 0 ||
      initialScrollPercent > 1
    ) {
      return;
    }
    if (typeof initialSceneIdxOneIndexed === 'number') {
      // Konflikt: Hash-Anchor gewinnt
      return;
    }
    const timer = window.setTimeout(() => {
      if (scrollSource === 'window') {
        const { max } = getWindowScrollMetrics();
        if (max <= 0) return;
        window.scrollTo(0, max * initialScrollPercent);
        return;
      }
      const el = viewerRootContainerRef.current;
      if (!el) return;
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) return;
      el.scrollTop = max * initialScrollPercent;
    }, LAYOUT_SETTLE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    initialScrollPercent,
    initialSceneIdxOneIndexed,
    scrollSource,
    viewerRootContainerRef,
  ]);

  // Plan-1.9 Phase 3.B.CP2 — initialSceneIdxOneIndexed Apply.
  useEffect(() => {
    if (typeof initialSceneIdxOneIndexed !== 'number') return;
    const timer = window.setTimeout(() => {
      const store = visualStoryStore.current;
      if (!store) return;
      const internalIdx = clampSceneIdxToZeroIndexed(
        initialSceneIdxOneIndexed,
        store.slideCount,
      );
      if (internalIdx === null) return;
      store.goToSlide(internalIdx);
    }, LAYOUT_SETTLE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [initialSceneIdxOneIndexed, visualStoryStore]);

  // Plan-1.9 Phase 3.A.CP3 — throttled scroll-progress callback.
  // Eigener scroll-listener am viewerRootContainerRef parallel zur
  // VisualStoryStore-internen syncTimerToScroll-RAF.
  useEffect(() => {
    if (!onScrollProgress) return;
    const el = viewerRootContainerRef.current;
    if (scrollSource === 'container' && !el) return;
    let progressFireTimer: number | null = null;
    let progressLastFiredAt = 0;
    const fireProgressNow = () => {
      const metrics =
        scrollSource === 'window'
          ? getWindowScrollMetrics()
          : {
              max: (el?.scrollHeight ?? 0) - (el?.clientHeight ?? 0),
              scrollTop: el?.scrollTop ?? 0,
            };
      const max = metrics.max;
      const percent =
        max > 0 ? Math.max(0, Math.min(1, metrics.scrollTop / max)) : 0;
      onScrollProgress(percent);
      progressLastFiredAt = Date.now();
      progressFireTimer = null;
    };
    const onScrollProgressEvent = () => {
      const elapsed = Date.now() - progressLastFiredAt;
      if (
        elapsed >= SCROLL_PROGRESS_THROTTLE_MS &&
        progressFireTimer === null
      ) {
        fireProgressNow();
      } else if (progressFireTimer === null) {
        progressFireTimer = window.setTimeout(
          fireProgressNow,
          Math.max(0, SCROLL_PROGRESS_THROTTLE_MS - elapsed),
        );
      }
    };
    const scrollTarget = scrollSource === 'window' ? window : el;
    scrollTarget?.addEventListener('scroll', onScrollProgressEvent, {
      passive: true,
    });
    return () => {
      if (progressFireTimer !== null) window.clearTimeout(progressFireTimer);
      scrollTarget?.removeEventListener('scroll', onScrollProgressEvent);
    };
  }, [onScrollProgress, scrollSource, viewerRootContainerRef]);

  // WEBKIT-KLEMME gegen den Footer-Rücksprung (Jans iPhone-Fund 26.08.2026,
  // per ?scrolldebug-Overlay überführt): Steht der Leser am Scroll-Maximum
  // (Footer sichtbar), setzt iOS-Safari die Container-Position nach einigen
  // Sekunden LEERLAUF selbsttätig um ~einen Viewport zurück — exakt an die
  // Stelle, an der die Footer-Oberkante die Viewport-Unterkante berührt
  // (Messung: Finger 8s oben, kein JS-Schreiber, kein Fokus, Gummiband
  // zuvor sauber auf max gesettelt, dann atomarer -1025px-Schlag; Ziel
  // reproduzierbar identisch). Vermutlich WebKit-Selbstkorrektur an der
  // Grenze des position:sticky-Bühnen-Containments bei einer verzögerten
  // Neuberechnung.
  //
  // Die Klemme stellt die Position wieder her, wenn ALLE Indizien auf den
  // Browser-Bug zeigen: Rückwärts-Einzelsprung >= 300 px AUS DER RUHE
  // (>= 400 ms kein Scroll-Ereignis davor), Finger oben, im Fußbereich
  // (letztes Viewport-Doppel). Echte Gesten sind ausgeschlossen: Momentum
  // liefert kontinuierliche Ereignisse ohne Ruhepause, Drags haben den
  // Finger unten, Tweens (Autoflug/Navigation) schreiben in kleinen
  // Schritten ebenfalls ohne Ruhepause.
  //
  // Nebenklemme am selben Listener-Satz: touchstart bricht einen laufenden
  // Scroll-Tween ab (START-Autoflug kämpfte sonst gegen den sofort
  // losscrollenden Nutzer — zweiter Fund derselben Messrunde).
  useEffect(() => {
    const el = viewerRootContainerRef.current;
    if (!el || typeof window === 'undefined') return;
    if (scrollSource === 'window') return; // Klemme gilt dem Container-Modus
    if (!('ontouchstart' in window) && navigator.maxTouchPoints === 0) return;
    let letzterTop = el.scrollTop;
    const aufTouchStart = () => {
      // Nutzer greift ein → Autoflug sofort beenden.
      (visualStoryStore.current as unknown as { cancelScroll?: () => void })
        ?.cancelScroll?.();
    };
    const aufScroll = () => {
      const top = el.scrollTop;
      const delta = top - letzterTop;
      const max = el.scrollHeight - el.clientHeight;
      // Signatur aus VIER Messrunden (Jans iPhone, ?scrolldebug-Overlay):
      // Der WebKit-Schlag startet IMMER von exakt max (Gummiband settelt
      // vorher in 1-px-Schritten dorthin) und wirft in EINEM Ereignis um
      // >= 300 px zurueck — mit Finger unten wie oben, im Leerlauf wie
      // direkt nach dem Settle. Eine echte Geste kann das nicht: Drags
      // und Momentum liefern kontinuierliche kleine Schritte (erst recht
      // vom Anschlag aus), und Tweens tragen die data-scrolly-tween-
      // Markierung (scrollTo.ts). Ein Footer-Anker-Abgleich scheiterte
      // daran, dass der oeffentliche Reader den Footer nicht ueber den
      // renderFooter-Slot mountet — die Absprung-Position ist der
      // robustere Fingerabdruck.
      const standAmMaximum = letzterTop >= max - 8;
      if (
        delta <= -300 &&
        standAmMaximum &&
        !el.hasAttribute('data-scrolly-tween')
      ) {
        el.scrollTop = letzterTop;
        // letzterTop bleibt das verteidigte Ziel
        return;
      }
      letzterTop = top;
    };
    el.addEventListener('touchstart', aufTouchStart, { passive: true });
    el.addEventListener('scroll', aufScroll, { passive: true });
    return () => {
      el.removeEventListener('touchstart', aufTouchStart);
      el.removeEventListener('scroll', aufScroll);
    };
  }, [scrollSource, viewerRootContainerRef]);

  const innerHeight = useSyncExternalStore<number>(
    (visualStoryStore.current &&
      visualStoryStore.current.subscribeToHeightChanges) ||
      dummy,
    (visualStoryStore.current && visualStoryStore.current.heightSnapshot) ||
      dummySnapShot,
    (visualStoryStore.current && visualStoryStore.current.heightSnapshot) ||
      dummySnapShot,
  );

  const headingFontFamily =
    fontFamilies.headlineFontFamily || VIEWER_FONT_FALLBACK;
  const bodyFontFamily = fontFamilies.textFontFamily || VIEWER_FONT_FALLBACK;

  useEffect(() => {
    if (embedded) return;
    document.documentElement.style.setProperty(
      '--font-heading',
      `'${headingFontFamily}'`,
    );
    document.documentElement.style.setProperty(
      '--font-body',
      `'${bodyFontFamily}'`,
    );
  }, [embedded, headingFontFamily, bodyFontFamily]);

  const wrapperStyle: CSSProperties = {
    position: 'relative',
    visibility: realHeight === null ? 'hidden' : 'visible',
  };
  if (embedded) {
    (wrapperStyle as Record<string, string>)['--font-heading'] =
      `'${headingFontFamily}'`;
    (wrapperStyle as Record<string, string>)['--font-body'] =
      `'${bodyFontFamily}'`;
  }

  // Cover-Slot: renderCover override-prop (public/) vs Default-StartScreen
  // (app/). embedded=true skipped beides.
  const coverElement = embedded ? null : renderCover !== undefined ? (
    renderCover({
      onStart: () => onStart(),
      progress,
      isOgMode,
      fontFamilies,
      height: innerHeight,
    })
  ) : (
    <StartScreen
      post={post}
      progress={progress}
      onStartClick={onStart}
      fontFamilies={fontFamilies}
      height={innerHeight}
      isOgMode={isOgMode}
    />
  );

  return (
    <div style={wrapperStyle}>
      {editorBridge ? editorBridge(renderer) : null}
      {/* COVER SOFORT SICHTBAR (Jans Slow-4G-Test 02.09.2026): Das
          visibility-Gate des Wrappers (realHeight-Messung braucht JS) hat
          das server-gerenderte Cover mit verschluckt — auf schmalem Netz
          stand sekundenlang ein schwarzer Kasten, obwohl Titel, START und
          Boot-Hintergrundbild laengst im HTML lagen. CSS-visibility laesst
          sich am Kind ueberstimmen: die BUEHNE bleibt bis zur Messung
          versteckt (kein Groessen-Springen), das Cover erscheint sofort.
          Der div ist bewusst ohne position/z-index — die absolute
          Positionierung des Covers bindet weiter an den Wrapper. */}
      <div style={{ visibility: 'visible' }}>{coverElement}</div>
      {!embedded && !isOgMode && !disableCompanyLogo && (
        <CompanyLogo post={post} brandingLogo={brandingLogo} />
      )}
      <VisualStoryPlayer
        mainContainerRef={mainContainerRef}
        post={post}
        visualStoryStore={currentStoryStore}
        showQuality={isAuthenticated}
        canvasContainerRef={canvasContainerRef}
        htmlContainerRef={htmlRendererRef}
        currentSlideIndex={currentSlideIndex}
        fontFamilies={fontFamilies}
        bodyScroll={scrollSource === 'window'}
        editorMode={isProEditor}
        runningCtaImRahmen={runningCtaImRahmen}
        ueberlagerungMassstab={ueberlagerungMassstab}
        onCtaAntwort={onCtaAntwort}
        kapitelWort={t('viewer.chapter', lang)}
      />
      {!embedded && !isOgMode && renderFooter}
    </div>
  );
}

export { ScrollyViewer };
export default ScrollyViewer;
